import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import {
  getCodeData,
  getInitForm,
  getInitSheet,
} from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnSheet from "@shared/components/Grid/SnSheet";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, Tooltip } from "@mui/material";

interface Props {
  Menuprops: menuInfo;
  PkFields: string[]; // PK 필드 목록
  tableName: string; // 저장할 테이블 이름
  controllerName: string; // 저장할 API 컨트롤러 이름
  pkCheckCode?: string; // PK 중복 체크에 사용할 코드/테이블명
  defaultValue?: Record<string, any>;
  // 업로드 시트 컬럼을 호출한 화면의 컬럼으로 사용할 경우 전달한다.
  // 전달하지 않으면 기존처럼 메뉴 메타(sheet1)를 조회해 구성한다.
  columnConfig?: any[];
  // 오류체크(필수값 검증) 대상 컬럼을 화면에서 지정할 경우 전달한다.
  // 전달하면 여기 있는 컬럼만 필수, 나머지는 필수 해제된다.
  requiredFields?: string[];
  // 있으면 [파일업로드] 버튼을 보이고, 돌려준 행(시트 컬럼명 기준)을 시트에 채운다
  onFileUpload?: () => Promise<any[] | null | undefined>;
  // true 면 [저장] 시 DB 저장 대신 시트 행을 { excelRows } 로 돌려준다 (화면 그리드에 적용용)
  returnRowsOnSave?: boolean;
  // 있으면 메뉴 sheet1 메타 대신 이 컬럼(화면 그리드 컬럼)으로 시트를 그린다
  sheetColumns?: any[];
}

const ExcelUpload = forwardRef(
  (
    {
      Menuprops,
      PkFields,
      tableName,
      controllerName,
      pkCheckCode,
      defaultValue,
      columnConfig,
      requiredFields,
      onFileUpload,
      returnRowsOnSave,
      sheetColumns,
    }: Props,
    ref: React.Ref<any>
  ) => {
    const ERROR_COL_ID = "__upload_error_summary";
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const [statusMessage, setStatusMessage] = useState<string>("");
    const [isValidating, setIsValidating] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");
    const gridref1 = useRef<any>(null);
    const errorModalResolverRef = useRef<(() => void) | null>(null);
    const codeDataCacheRef = useRef<Map<string, any[]>>(new Map());
    const popupDataCacheRef = useRef<Map<string, any[]>>(new Map());
    let SaveData: any = {};

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const [compareFields, setCompareFields] = useState<string[]>(PkFields); // 비교할 필드를 상태로 저장
    const [codeDataList, setCodeDataList] = useState<Record<string, string>[]>(
      []
    ); // 비교 대상 테이블 data tableName

    //  tableName list

    //  메뉴에서 `cd_clas`, `cd_popup` 데이터를 한 번만 로드
    const [codeDataMap, setCodeDataMap] = useState(new Map());
    const [popupDataMap, setPopupDataMap] = useState(new Map());

    //#endregion

    // requiredFields 를 넘기면 그 컬럼만 필수(오류체크 대상)로 두고 나머지는 해제한다.
    const applyRequiredFields = useCallback(
      (baseCols: any[]) => {
        const cols = Array.isArray(baseCols) ? baseCols : [];
        if (!requiredFields || requiredFields.length === 0) return cols;
        return cols.map((col: any) => ({
          ...col,
          yn_required: requiredFields.includes(col?.cd_column) ? "Y" : "N",
        }));
      },
      [requiredFields]
    );

    const ensureErrorColumn = useCallback((baseCols: any[]) => {
      const cols = Array.isArray(baseCols) ? baseCols : [];
      if (cols.some((c: any) => c.cd_column === ERROR_COL_ID)) return cols;
      const errorCol = {
        cd_column: ERROR_COL_ID,
        nm_column: "오류내용",
        tp_column: "SnTextBox",
        dc_editcon: "true",
        fg_visible: "Y",
        yn_required: "N",
        qt_width: 320,
      };
      return [...cols, errorCol];
    }, []);

    useImperativeHandle(ref, () => ({
      onDelete,
      onSave,
      getSelectedValue: async () => {
        if (returnRowsOnSave) {
          const excelRows = collectNonEmptyRows().map((row) => {
            const next = { ...row };
            delete next[ERROR_COL_ID];
            delete next.KeyField;
            return next;
          });
          return { excelRows };
        }
        const ok = await onSave();
        return { excelUpload: !!ok };
      },
      getSelectedRow: () => {
        const gridData: any[] = [];
        gridref1.current.api.forEachNode((node: any) => {
          gridData.push(node.data);
        });
        return gridData;
      },
    }));

    useEffect(() => {
      const fetchform = async () => {
        setStatusMessage("초기화 중...");
        setIsResetting(true);
        try {
          // 화면에서 컬럼을 전달하면 그대로 사용, 없으면 메뉴 메타(sheet1) 조회
          // columnConfig(단가등록) → sheetColumns(경비등록) → 메뉴 메타 순으로 사용한다.
          const baseCols =
            Array.isArray(columnConfig) && columnConfig.length > 0
              ? columnConfig
              : sheetColumns
              ? sheetColumns.map((col: any) => ({ ...col }))
              : ((await getInitSheet({})) as any)?.grid?.sheet1 ?? [];

          const ensuredCols = ensureErrorColumn(applyRequiredFields(baseCols));
          setColums(ensuredCols);
          // 최초 오픈에서 붙여넣기 가능한 기본 row 보장
          const apiReady = await waitForGridApiReady();
          if (
            apiReady &&
            (gridref1.current?.api?.getDisplayedRowCount?.() ?? 0) === 0
          ) {
            await gridref1.current.resetGridData();
            await waitForRowsReady(1);
          }
          await waitForColumnsReady(Math.max(1, ensuredCols.length));
        } catch (error) {
        } finally {
          setIsResetting(false);
          setStatusMessage("");
        }
      };
      fetchform();
    }, []);

    useEffect(() => {
      if (gridref1.current) {
        gridref1.current.setCodeDataList(codeDataList); //  SnSheet 내부에서 코드 데이터 갱신 메서드 실행
      }
    }, [codeDataList]); //  codeDataList가 변경될 때마다 실행

    const onDelete = () => {
      const focusedCell = gridref1.current.api.getFocusedCell();
      let startRowIndex = focusedCell.rowIndex ?? 0;

      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    const runWithStatus = async <T,>(
      message: string,
      task: () => Promise<T>
    ): Promise<T> => {
      setStatusMessage(message);
      try {
        setStatusMessage("유효성 확인 중...");
        return await task();
      } finally {
        setStatusMessage("");
      }
    };

    const setOverlayMessage = (message: string) => {
      const api = gridref1.current?.api;
      if (!api) return;
      (api as any).__loadingMessage = message;
      const template = `<span class="ag-overlay-loading-center">${message}</span>`;
      api.setGridOption?.("overlayLoadingTemplate", template);
      api.setOverlayLoadingTemplate?.(template);
    };

    const showLocalErrorModal = useCallback((message: string) => {
      setErrorModalMessage(message);
      setErrorModalOpen(true);
      return new Promise<void>((resolve) => {
        errorModalResolverRef.current = resolve;
      });
    }, []);

    const closeLocalErrorModal = useCallback(() => {
      setErrorModalOpen(false);
      const resolve = errorModalResolverRef.current;
      errorModalResolverRef.current = null;
      // 닫힘 페인트를 먼저 보장하고 후속 검증 로직을 다음 tick으로 넘긴다.
      setTimeout(() => {
        resolve?.();
      }, 0);
    }, []);

    const waitForGridApiReady = useCallback(async () => {
      // 팝업 오픈 직후 grid api 준비 지연이 있어 메시지가 먼저 사라지는 문제를 방지한다.
      for (let i = 0; i < 40; i += 1) {
        if (gridref1.current?.api) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    const waitForRowsReady = useCallback(async (minRows: number = 1) => {
      // resetGridData 완료 후에도 렌더 반영이 한 템포 늦을 수 있어 표시 row를 확인한다.
      for (let i = 0; i < 40; i += 1) {
        const cnt = gridref1.current?.api?.getDisplayedRowCount?.() ?? 0;
        if (cnt >= minRows) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    const waitForColumnsReady = useCallback(async (minCols: number = 1) => {
      // row가 먼저 보이고 컬럼이 나중에 생성되는 구간이 있어 컬럼 개수로 완료를 판단한다.
      for (let i = 0; i < 80; i += 1) {
        const cols = gridref1.current?.api?.getColumns?.() ?? [];
        if (cols.length >= minCols) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    const normalizeDateValue = (value: unknown) => {
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      if (!trimmed) return value;
      return trimmed.replace(/[^\d]/g, "");
    };

    const normalizeRowForSave = (
      row: Record<string, any>,
      codeMap: Map<string, any[]>,
      popupMap: Map<string, any[]>
    ) => {
      const next = { ...row };
      delete next[ERROR_COL_ID];
      const firm = global.getState().userInfo?.cd_firm;
      if (
        firm &&
        (next.cd_firm == null || String(next.cd_firm).trim() === "")
      ) {
        next.cd_firm = firm;
      }
      Object.keys(next).forEach((key) => {
        if (key.startsWith("dt_")) {
          next[key] = normalizeDateValue(next[key]);
        }
      });
      (columns ?? []).forEach((col: any) => {
        const field = String(col.cd_column ?? "").trim();
        if (!field || next[field] == null || next[field] === "") return;

        if (col.tp_column === "SnComboBox" && col.cd_clas) {
          const list = codeMap.get(col.cd_clas) ?? [];
          const match = list.find(
            (item: any) =>
              item.code === next[field] || item.name === next[field]
          );
          if (match) {
            next[field] = match.code;
            const displayField =
              col.cd_displaycolumn ||
              (field.startsWith("cd_") ? field.replace(/^cd_/, "nm_") : "");
            if (displayField && displayField !== field) {
              next[displayField] = match.name;
            }
          }
        }

        if (col.tp_column === "SnCodeBox" && col.cd_popup) {
          const list = popupMap.get(col.cd_popup) ?? [];
          const match = list.find(
            (item: any) =>
              item.code === next[field] || item.name === next[field]
          );
          if (match) {
            next[field] = match.code;
            const displayField =
              col.cd_displaycolumn || field.replace(/^cd_/, "nm_");
            if (displayField && displayField !== field) {
              next[displayField] = match.name;
            }
          }
        }
      });
      return next;
    };

    const buildSavePayload = async (change: {
      insert?: any[];
      update?: any[];
      delete?: any[];
      modifiedRowcount?: number;
    }) => {
      const uniqueCdClas = Array.from(
        new Set(
          (columns ?? [])
            .filter((col: any) => col.cd_clas && col.tp_column === "SnComboBox")
            .map((col: any) => String(col.cd_clas))
        )
      );
      const uniqueCdPopup = Array.from(
        new Set(
          (columns ?? [])
            .filter((col: any) => col.cd_popup && col.tp_column === "SnCodeBox")
            .map((col: any) => String(col.cd_popup))
        )
      );
      const codeMap = new Map<string, any[]>();
      const popupMap = new Map<string, any[]>();
      for (const cd of uniqueCdClas) {
        if (!codeDataCacheRef.current.has(cd)) {
          codeDataCacheRef.current.set(cd, await getCodeData(cd, ""));
        }
        codeMap.set(cd, codeDataCacheRef.current.get(cd) ?? []);
      }
      for (const cd of uniqueCdPopup) {
        if (!popupDataCacheRef.current.has(cd)) {
          popupDataCacheRef.current.set(cd, await getCodeData(cd, ""));
        }
        popupMap.set(cd, popupDataCacheRef.current.get(cd) ?? []);
      }

      const insert = (change.insert ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      const update = (change.update ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      const del = (change.delete ?? []).map((row) =>
        normalizeRowForSave(row, codeMap, popupMap)
      );
      return {
        insert,
        update,
        delete: del,
        modifiedRowcount: insert.length + update.length + del.length,
      };
    };

    const collectNonEmptyRows = () => {
      const api = gridref1.current?.api;
      if (!api) return [];
      const fields = (columns ?? [])
        .map((col: any) => String(col.cd_column ?? "").trim())
        .filter((field) => field.length > 0);
      const hasValue = (value: any) =>
        value === 0 ||
        value === false ||
        (value != null && String(value).trim() !== "");
      const rows: any[] = [];
      api.forEachNode((node: any) => {
        const row = node?.data;
        if (!row) return;
        if (fields.length > 0) {
          if (fields.some((field) => hasValue(row[field]))) {
            rows.push({ ...row });
          }
        } else if (
          Object.entries(row).some(
            ([key, value]) => key !== "KeyField" && hasValue(value)
          )
        ) {
          rows.push({ ...row });
        }
      });
      return rows;
    };

    const onSave = async (): Promise<boolean> => {
      try {
        // 편집 중인 셀을 먼저 확정해야 마지막 입력값이 저장/검증에 반영된다.
        gridref1.current?.api?.stopEditing?.();
        await new Promise((resolve) => setTimeout(resolve, 0));

        // 중앙 로딩 오버레이 문구만 사용
        gridref1.current?.setLoadingMessage?.("유효성 확인 중...");
        await new Promise((resolve) => setTimeout(resolve, 0));
        (gridref1.current?.api as any).__suppressLoadingOverlay = true;
        (gridref1.current?.api as any).__preserveChangesOnValidate = true;
        (gridref1.current?.api as any).__validateChangedOnly = true;

        const pkCheckKey = pkCheckCode ?? tableName;
        let change = gridref1.current.getChange();
        const fallbackRows =
          change.modifiedRowcount === 0 ? collectNonEmptyRows() : [];
        if (change.modifiedRowcount === 0 && fallbackRows.length === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }
        if (change.modifiedRowcount === 0 && fallbackRows.length > 0) {
          // defaultValue 는 아래에서 빈 값만 채워 넣는다.
          // (여기서 Object.assign 으로 덮어쓰면 엑셀에 입력한 값이 기본값으로 바뀐다)
          change = {
            insert: fallbackRows,
            update: [],
            delete: [],
            modifiedRowcount: fallbackRows.length,
          };
        }

        // defaultValue 는 엑셀에서 비워둔 값만 채운다. (입력값은 유지)
        // PK 판정에 쓰이는 값(cd_module 등)이 있으므로 중복 판정 전에 적용한다.
        if (defaultValue) {
          const isEmpty = (v: any) => v == null || String(v).trim() === "";
          [...(change.insert ?? []), ...(change.update ?? [])].forEach(
            (row: any) => {
              Object.entries(defaultValue).forEach(([key, value]) => {
                if (isEmpty(row[key])) row[key] = value;
              });
            }
          );
        }

        const v1 = await Validation(false);
        if (!v1) return false;

        // const v2 = await Validation(true);
        // if (!v2) return false;
        (gridref1.current?.api as any).__suppressLoadingOverlay = false;
        (gridref1.current?.api as any).__preserveChangesOnValidate = false;
        (gridref1.current?.api as any).__validateChangedOnly = false;

        const existingRows = await getCodeData(pkCheckKey, "");
        const normalizeKey = (row: any) =>
          PkFields.map((field) => {
            let value = row?.[field];
            if (
              (value == null || String(value).trim() === "") &&
              field === "cd_firm"
            ) {
              value = global.getState().userInfo?.cd_firm;
            }
            return value == null ? "" : String(value).trim();
          }).join("|");
        const existingKeySet = new Set(
          (existingRows ?? []).map((row: any) => normalizeKey(row))
        );

        // 그리드 분류(insert/update)와 무관하게 DB 조회 결과(PK) 기준으로 다시 판정한다.
        const inserts: any[] = [];
        const dupes: any[] = [];
        [...(change.insert ?? []), ...(change.update ?? [])].forEach(
          (row: any) => {
            if (existingKeySet.has(normalizeKey(row))) {
              dupes.push(row);
            } else {
              inserts.push(row);
            }
          }
        );

        // 이미 저장된 행이 있으면 업데이트 여부를 묻고,
        // '아니오'면 해당 행만 제외한 뒤 신규 행만 저장한다.
        let finalUpdates: any[] = [];
        if (dupes.length > 0) {
          const doUpdate = await showDialog(
            "confirm",
            `이미 저장된 내용이 ${dupes.length}건 있습니다. 업데이트 하시겠습니까?
(아니오를 선택하면 해당 행은 제외하고 신규 ${inserts.length}건만 저장합니다.)`
          );
          if (doUpdate) finalUpdates = dupes;
        }

        change = {
          ...change,
          insert: inserts,
          update: finalUpdates,
          modifiedRowcount: inserts.length + finalUpdates.length,
        };

        if (change.modifiedRowcount === 0) {
          await showDialog("alter", "저장할 내용이 없습니다.");
          gridref1.current?.api?.hideOverlay?.();
          return false;
        }

        gridref1.current?.setLoadingMessage?.("업로드 저장 중...");
        await new Promise((resolve) => setTimeout(resolve, 0));

        SaveData[tableName] = await buildSavePayload(change);
        console.log("SAVE payload", SaveData);

        const rtn = await fnSave(controllerName, SaveData);
        if (!rtn) {
          gridref1.current?.api?.hideOverlay?.();
          return false;
        }
        gridref1.current.resetUpdate();
        SaveData = [];
        gridref1.current?.api?.hideOverlay?.();
        return true;
      } catch (error: any) {}
      (gridref1.current?.api as any).__suppressLoadingOverlay = false;
      (gridref1.current?.api as any).__preserveChangesOnValidate = false;
      (gridref1.current?.api as any).__validateChangedOnly = false;
      gridref1.current?.api?.hideOverlay?.();
      return false;
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    const Validation = async (updatevalue: boolean): Promise<boolean> => {
      // 편집 중인 셀은 아직 데이터에 반영되지 않는다.
      // 값을 입력한 직후 오류체크를 누르면 빈 값으로 검사돼 계속 오류가 뜬다.
      gridref1.current?.api?.stopEditing?.();
      await new Promise((resolve) => setTimeout(resolve, 0));

      setIsValidating(true);
      //  Validation 실행하여 오류 체크
      // debugger;
      // 기존 데이터 조회가 실패해도(코드 미등록/SQL 오류) 필수값·형식 검증은 계속 되어야 한다.
      // 실패 시 예외가 그대로 올라가면 검증이 시작도 못 하고 조용히 통과된 것처럼 보인다.
      let data: any = [];
      try {
        data = (await getCodeData(pkCheckCode ?? tableName, "")) ?? []; //  data insert update체크할 데이터 조회
      } catch (error) {
        console.error("기존 데이터 조회 실패(PK 중복 판정 생략):", error);
        data = [];
      }
      setCodeDataList(data); // 기존 데이터 목록 저장

      // 검증 시작 시 오버레이 제어 플래그를 항상 초기화해서
      // 2회차 검증에서도 "유효성 확인 중..."이 확실히 보이게 한다.
      if (gridref1.current?.api) {
        const api: any = gridref1.current.api;
        api.__suppressLoadingOverlay = true;
        api.__keepOverlayUntilCallerDone = true;
        // 오류체크 버튼은 항상 전체 행을 다시 검증한다.
        api.__validateChangedOnly = false;
        // 입력이 하나도 없으면 첫 행만 검증해 그냥 통과되지 않도록 한다.
        api.__validateFirstRowWhenAllBlank =
          !!requiredFields && requiredFields.length > 0;
      }
      // SnSheet(showLoading)가 읽는 로딩 메시지 동기화

      if (gridref1.current.api.getDisplayedRowCount() <= 1) {
        await showDialog("info", "변경된 내용이 없습니다.");
        return true;
      }

      try {
        const isValid = await gridref1.current.validateData(updatevalue);
        // 유효성 검증 단계가 끝난 시점에 오버레이를 내린다.

        if (!isValid) {
          await showLocalErrorModal("오류내역을 확인해주세요.");
          return false;
        } else {
          return true;
        }
      } finally {
        if (gridref1.current?.api) {
          const api: any = gridref1.current.api;
          api.__suppressLoadingOverlay = false;
          api.__keepOverlayUntilCallerDone = false;
          api.__validateFirstRowWhenAllBlank = false;
        }
        setIsValidating(false);
      }
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_FileUpload" && onFileUpload) {
        const rows = await onFileUpload();
        if (Array.isArray(rows) && rows.length > 0) {
          setDataSource1(rows.map((row) => ({ ...row })));
        }
      }

      if (id == "aButton_Init") {
        setStatusMessage("초기화 중...");
        setIsResetting(true);
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
          if (gridref1.current?.api) {
            const api: any = gridref1.current.api;
            api.__skipVisualLoadingOverlay = true;
            // 초기화 후에는 변경분 검증 플래그를 리셋한다.
            api.__validateChangedOnly = false;
          }
          await gridref1.current.resetGridData();
          await waitForRowsReady(1);
          // setDataValue 는 편집 이벤트를 타면서 행이 재배치될 수 있어
          // 표시용 오류내용은 데이터만 비우고 리프레시한다.
          gridref1.current?.api?.forEachNode((node: any) => {
            if (!node?.rowPinned && node?.data) {
              node.data[ERROR_COL_ID] = "";
            }
          });
          gridref1.current?.api?.refreshCells({ force: true });
        } finally {
          if (gridref1.current?.api) {
            (gridref1.current.api as any).__skipVisualLoadingOverlay = false;
          }
          setIsResetting(false);
          setStatusMessage("");
        }
      }

      if (id == "aButton_Validation") {
        setStatusMessage("유효성 확인 중...");
        try {
          const isValid = await Validation(false);
          if (isValid) {
            showDialog("info", "유효성 체크가 완료되었습니다.");
          }
        } catch (error: any) {
          // 검증 중 예외를 삼키면 아무 반응 없이 통과한 것처럼 보인다.
          showDialog("error", error?.message ?? "유효성 검사에 실패했습니다.");
        } finally {
          setStatusMessage("");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <div style={{ position: "relative", height: "100%" }}>
              <SnSheet
                ref={gridref1}
                enableAddRow={true}
                columnConfig={columns}
                rowData={dataSource1}
                isEditable={true} // 수정 가능여부
                codeDataList={codeDataList} // pk 체크용 데이터 전달
                compareFields={compareFields} // 비교할 필드 정보 전달
                title={Menuprops.nm_menu}
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "nowrap",
                    }}
                  >
                    {onFileUpload && (
                      <SnButton
                        text={"파일업로드"}
                        onClick={() => {
                          onGrid1BtnsClick("aButton_FileUpload");
                        }}
                      />
                    )}
                    <SnButton
                      text={"초기화"}
                      onClick={() => {
                        onGrid1BtnsClick("aButton_Init");
                      }}
                    />
                    <SnButton
                      text={"오류체크"}
                      onClick={() => {
                        onGrid1BtnsClick("aButton_Validation");
                      }}
                    />

                    {statusMessage && (
                      <span
                        style={{
                          marginLeft: 8,
                          color: "gray",
                          fontSize: 12,
                        }}
                      >
                        {statusMessage}
                      </span>
                    )}
                    {false && (
                      <IconButton
                        onClick={onSave}
                        sx={{
                          color: "gray", // 기본 색상
                          "&:hover": {
                            color: "blue", // hover 시 색상
                            transform: "scale(1.2)", // hover 시 크기 확대
                            transition: "all 0.3s ease", // 부드러운 애니메이션
                          },
                        }}
                      >
                        <SaveIcon />
                      </IconButton>
                    )}

                    <Tooltip title="Delete">
                      <IconButton
                        onClick={onDelete}
                        sx={{
                          color: "gray", // 기본 색상
                          "&:hover": {
                            color: "blue", // hover 시 색상
                            transform: "scale(1.2)", // hover 시 크기 확대
                            transition: "all 0.3s ease", // 부드러운 애니메이션
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                }
              />
              {(isValidating || isResetting) && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.45)",
                    zIndex: 30,
                    pointerEvents: "none",
                    fontSize: 14,
                    color: "#1f2937",
                    fontWeight: 600,
                  }}
                >
                  {isResetting ? "초기화 중..." : "유효성 확인 중..."}
                </div>
              )}
            </div>
          </SnResizableLNR>
        </div>
        {errorModalOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.28)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                width: 320,
                background: "#fff",
                borderRadius: 6,
                boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
                padding: "16px 16px 12px",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 10 }}>오류</div>
              <div style={{ fontSize: 14, marginBottom: 16 }}>
                {errorModalMessage}
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="button"
                  onClick={closeLocalErrorModal}
                  style={{
                    minWidth: 80,
                    height: 32,
                    border: "1px solid #bdbdbd",
                    borderRadius: 4,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ExcelUpload;
